import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Check, Loader2, RotateCcw, X } from 'lucide-react'
import toast from 'react-hot-toast'
import api, { getApiError } from '../services/api'
import { useAuthStore } from '../stores/authStore'

const hoje = () => {
  const data = new Date()
  const ano = data.getFullYear()
  const mes = String(data.getMonth() + 1).padStart(2, '0')
  const dia = String(data.getDate()).padStart(2, '0')
  return `${ano}-${mes}-${dia}`
}

const formatarData = valor => new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: 'long'
}).format(new Date(`${valor}T12:00:00`))

function RevisaoPage() {
  const queryClient = useQueryClient()
  const { usuario, accessToken } = useAuthStore()
  const [mostrarResposta, setMostrarResposta] = useState(false)
  const [revisados, setRevisados] = useState(0)

  const {
    data: revisoes = [],
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: ['revisoes', 'pendentes'],
    queryFn: async () => (await api.get('/revisoes', { params: { status: 'pendente' } })).data.revisoes,
    enabled: Boolean(accessToken)
  })

  const revisoesHoje = useMemo(
    () => revisoes.filter(revisao => revisao.data_revisao <= hoje()),
    [revisoes]
  )
  const revisoesFuturas = useMemo(
    () => revisoes.filter(revisao => revisao.data_revisao > hoje()),
    [revisoes]
  )
  const revisaoAtual = revisoesHoje[0]
  const totalSessao = revisados + revisoesHoje.length
  const resumoSessao = `${revisados} ${revisados === 1 ? 'revisão concluída' : 'revisões concluídas'} nesta sessão`

  const concluirRevisao = useMutation({
    mutationFn: avaliacao => api.post(`/revisoes/${revisaoAtual.id}/concluir`, { avaliacao }),
    onSuccess: ({ data }) => {
      queryClient.setQueryData(
        ['revisoes', 'pendentes'],
        (atuais = []) => atuais.filter(revisao => revisao.id !== revisaoAtual.id)
      )
      setRevisados(valor => valor + 1)
      setMostrarResposta(false)
      toast.success(`Próxima revisão: ${formatarData(data.proxima_revisao.data_revisao)}`)
      queryClient.invalidateQueries({ queryKey: ['revisoes'] })
    },
    onError: error => toast.error(getApiError(error, 'Não foi possível concluir a revisão.'))
  })

  if (!usuario) {
    return (
      <div className="card text-center py-8">
        <RotateCcw size={24} className="mx-auto mb-3 text-gray-300" />
        <p className="text-sm text-gray-600 mb-3">Entre para acessar sua fila de revisão.</p>
        <Link to="/login" className="btn-primary inline-block">Entrar</Link>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="card flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
        <Loader2 size={17} className="animate-spin" /> Carregando revisões...
      </div>
    )
  }

  if (isError) {
    return (
      <div className="card text-center py-8">
        <p className="text-sm text-gray-600 mb-3">Não foi possível carregar suas revisões.</p>
        <button type="button" onClick={() => refetch()} className="btn-secondary">Tentar novamente</button>
      </div>
    )
  }

  if (!revisaoAtual) {
    const proxima = revisoesFuturas[0]

    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <div>
          <h1 className="text-lg font-medium">Revisão Espaçada</h1>
          <p className="text-xs text-gray-500">Revise no momento certo para lembrar por mais tempo.</p>
        </div>
        <div className="card flex flex-col items-center justify-center py-12 text-center">
          <RotateCcw size={40} className="text-gray-300 mb-4" />
          <h2 className="text-base font-medium mb-2">
            {proxima ? 'Revisões de hoje concluídas' : 'Nenhuma revisão agendada'}
          </h2>
          <p className="text-sm text-gray-500 max-w-xs">
            {proxima
              ? `Sua próxima revisão está marcada para ${formatarData(proxima.data_revisao)}.`
              : 'Abra um tema de estudo e toque em “Adicionar à revisão”.'}
          </p>
          {!proxima && <Link to="/" className="btn-primary mt-4">Escolher um tema</Link>}
          {revisados > 0 && <p className="mt-4 text-xs text-emerald-600">{resumoSessao}</p>}
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-medium lg:text-2xl">Revisão Espaçada</h1>
          <p className="text-xs text-gray-500">Tente recordar antes de revelar o resumo.</p>
        </div>
        <span className="text-xs text-gray-500 whitespace-nowrap">
          {revisados + 1} / {totalSessao}
        </span>
      </div>

      <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-black rounded-full transition-all"
          style={{ width: `${((revisados + 1) / totalSessao) * 100}%` }}
        />
      </div>

      <button
        type="button"
        onClick={() => setMostrarResposta(valor => !valor)}
        className="flex min-h-[220px] w-full flex-col items-center justify-center rounded-2xl border-2 border-gray-100 p-6 text-left transition-all hover:border-gray-200 active:scale-[0.99] lg:min-h-[340px] lg:p-10"
      >
        <span className="text-xs text-gray-400 mb-4">{revisaoAtual.tema?.nome}</span>
        <p className="text-center text-lg font-medium leading-relaxed">
          {mostrarResposta
            ? revisaoAtual.tema?.descricao || 'Revise suas anotações e os principais conceitos deste tema.'
            : `O que você lembra sobre ${revisaoAtual.tema?.nome}?`}
        </p>
        <span className="text-xs text-gray-400 mt-4">
          {mostrarResposta ? 'Toque para ocultar' : 'Toque para revelar o resumo'}
        </span>
      </button>

      {mostrarResposta && (
        <div className="grid grid-cols-4 gap-2">
          <button
            type="button"
            onClick={() => concluirRevisao.mutate('erro')}
            disabled={concluirRevisao.isLoading}
            className="flex flex-col items-center gap-1 py-3 rounded-xl bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
          >
            <X size={20} />
            <span className="text-[10px] font-medium">Errei</span>
          </button>
          <button
            type="button"
            onClick={() => concluirRevisao.mutate('dificil')}
            disabled={concluirRevisao.isLoading}
            className="flex flex-col items-center gap-1 py-3 rounded-xl bg-orange-50 text-orange-700 hover:bg-orange-100 disabled:opacity-50"
          >
            <AlertCircle size={20} />
            <span className="text-[10px] font-medium">Difícil</span>
          </button>
          <button
            type="button"
            onClick={() => concluirRevisao.mutate('bom')}
            disabled={concluirRevisao.isLoading}
            className="flex flex-col items-center gap-1 py-3 rounded-xl bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
          >
            <Check size={20} />
            <span className="text-[10px] font-medium">Bom</span>
          </button>
          <button
            type="button"
            onClick={() => concluirRevisao.mutate('facil')}
            disabled={concluirRevisao.isLoading}
            className="flex flex-col items-center gap-1 py-3 rounded-xl bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
          >
            <Check size={20} />
            <span className="text-[10px] font-medium">Fácil</span>
          </button>
        </div>
      )}

      <div className="text-center text-xs text-gray-400">
        {resumoSessao}
      </div>
    </div>
  )
}

export default RevisaoPage
